import CodecParser, { CodecFrame, MPEGHeader } from "codec-parser"

/**
 * The Xing / Info tag is a 4-byte ASCII marker embedded in the first MPEG audio
 * frame of files encoded by LAME, FFmpeg, and similar tools.  The frame is
 * structurally valid MPEG audio but carries no audible samples — it only stores
 * VBR seek tables, encoder settings, and gapless playback metadata.
 */
function isXingOrInfoFrame(mp3Data: Uint8Array, frame: { totalBytesOut: number; data: { length: number } }): boolean {
  const start = frame.totalBytesOut
  const end = Math.min(start + frame.data.length, mp3Data.length)

  // Search the frame payload for the 4-byte ASCII tag.  The exact offset
  // depends on the MPEG version, layer, and channel mode, so a short scan
  // is more robust than computing the side-information length.
  for (let i = start; i <= end - 4; i++) {
    if (
      (mp3Data[i] === 0x58 && mp3Data[i + 1] === 0x69 && mp3Data[i + 2] === 0x6E && mp3Data[i + 3] === 0x67) || // "Xing"
      (mp3Data[i] === 0x49 && mp3Data[i + 1] === 0x6E && mp3Data[i + 2] === 0x66 && mp3Data[i + 3] === 0x6F)    // "Info"
    ) {
      return true
    }
  }

  return false
}

/**
 * Compact per-frame record used for seeking.
 *
 * Each entry represents one MPEG audio frame in the file and stores enough
 * information to jump to a byte offset and know exactly how many PCM samples
 * precede it.
 */
export interface MpegFrameEntry {
  byteOffset: number
  cumulativeSamples: number
  frameLength: number
  samples: number
}

/**
 * Parsed, seekable view of a single MPEG audio region.
 *
 * This class mirrors `OggOpusIndex` in spirit: it does all container-level
 * work once up front — parsing MPEG frames via `codec-parser`, recording byte
 * offsets, cumulative sample positions, sample rate, channel count, and total
 * duration — so `Mp3RegionDecoder` can seek without reparsing.
 */
export class MpegAudioIndex {
  readonly channels: number
  readonly durationSeconds: number
  readonly frames: MpegFrameEntry[]
  readonly sampleRate: number
  readonly totalSamples: number

  private readonly rawData: Uint8Array

  constructor(mp3Data: Uint8Array) {
    this.rawData = mp3Data

    const parser = new CodecParser<CodecFrame>("audio/mpeg", {
      enableFrameCRC32: false,
    })

    const parsedFrames = [
      ...parser.parseAll(mp3Data),
    ]

    if (parsedFrames.length === 0) {
      throw new Error("No MPEG audio frames found")
    }

    const firstHeader = parsedFrames[0].header as MPEGHeader
    this.sampleRate = firstHeader.sampleRate
    this.channels = firstHeader.channels

    const frames: MpegFrameEntry[] = []
    let cumulativeSamples = 0

    for (let i = 0; i < parsedFrames.length; i++) {
      const frame = parsedFrames[i]

      // The first frame in a LAME/FFmpeg-encoded MP3 is typically a Xing or
      // Info metadata frame.  It carries no audible samples and the decoder
      // never produces output for it.  Including it in the index would shift
      // every subsequent frame's cumulativeSamples by one frame's worth of
      // phantom samples, causing seek misalignment.
      if (i === 0 && isXingOrInfoFrame(mp3Data, frame)) {
        continue
      }

      frames.push({
        byteOffset: frame.totalBytesOut,
        cumulativeSamples,
        frameLength: frame.data.length,
        samples: frame.samples,
      })
      cumulativeSamples += frame.samples
    }

    this.frames = frames
    this.totalSamples = cumulativeSamples
    this.durationSeconds = cumulativeSamples / this.sampleRate
  }

  /**
   * Returns the raw MPEG data for a contiguous range of frames.
   *
   * The decoder uses this to feed frame data to `mpg123-decoder` for a
   * seek + decode operation.
   */
  getFrameData(frameIndex: number): Uint8Array {
    const frame = this.frames[frameIndex]
    if (!frame) {
      throw new RangeError(`Frame index ${frameIndex} out of range`)
    }
    return this.rawData.subarray(frame.byteOffset, frame.byteOffset + frame.frameLength)
  }

  /**
   * Finds the frame index whose cumulative sample range contains the target
   * sample position, or the nearest frame at or before it.
   *
   * Used by the decoder to anchor a seek: back up by preroll frames from this
   * anchor, decode forward, then drop preroll samples.
   */
  findFrameIndexAtOrBeforeSample(targetSample: number): number {
    if (targetSample <= 0) {
      return 0
    }

    let left = 0
    let right = this.frames.length - 1
    let result = 0

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      if (this.frames[mid].cumulativeSamples <= targetSample) {
        result = mid
        left = mid + 1
      } else {
        right = mid - 1
      }
    }

    return result
  }
}
